import {
  BuildingOfficeIcon,
  CardsThreeIcon,
  ChartLineIcon,
  ChatsIcon,
  GearIcon,
  GitForkIcon,
  LightningIcon,
  UsersThreeIcon,
  WrenchIcon,
} from '@phosphor-icons/react'
import { createElement } from 'react'
import { isSMCOrg } from './isSMCOrg'
import { isSystemPipelineId } from 'app/components/action-center/systemPipelines'
import AgentsIcon from 'app/components/Icon/AgentsIcon'
import { legacyRoleRank } from 'app/types/roles'
import { hasRole } from 'app/utils'
import { customListItem, RECORD_TYPES } from 'lib/recordTypes'
import type { NavItemsInput, RecordTypeDef } from 'app/types/navigation'
import type { LocationProductType } from 'app/types/entities'
import { NavItemType } from 'types/Sidebar'

/**
 * Check if a nav item's product requirement is met. An array means OR: any match shows the item.
 *
 * Mirrors the legacy (`main`) gate semantics (TRK-5965):
 * - No product requirement on the item -> always show.
 * - Not in a workspace (org-level user) -> show. Legacy returns `true` for `!workspace`, because
 *   products are a workspace concept and org users navigate above them.
 * - In a workspace but `products` not loaded yet (`undefined`) -> show. This is the TRK-5159
 *   flicker guard: do not hide items (e.g. Settings > Integrations) while me.products resolves.
 *   An empty array is distinct — it means "loaded, this workspace has no products".
 * - In a workspace with loaded products -> strict membership. An empty array hides CRM nav for an
 *   AI-agent-only client, matching legacy `workspace.products.includes('CRM') === false`.
 */
export function hasNavProduct(
  products: string[] | undefined,
  product: string | string[] | undefined,
  inWorkspace: boolean,
): boolean {
  if (!product) return true
  if (!inWorkspace) return true
  if (products === undefined) return true
  if (Array.isArray(product)) return product.some((p) => products.includes(p))
  return products.includes(product)
}

function recordTypeGroup(recordType: RecordTypeDef, input: NavItemsInput, children: NavItemType[]): NavItemType {
  return {
    name: recordType.label,
    icon: recordType.icon,
    ...recordType.gate(input),
    active: !!input.location.pathname.match(recordType.path),
    children: [
      ...children,
      ...(recordType.customPageType ? input.customPages[recordType.customPageType] : []).map((page) =>
        customListItem(recordType, page, input),
      ),
    ],
    canCreateNewList: true,
  }
}

export function actionCenterItem(input: NavItemsInput): NavItemType {
  const { workspace, location } = input
  return {
    name: `Action Center`,
    icon: () => createElement(LightningIcon, { size: 20, weight: `bold` }),
    href: `/action-center`,
    if: !!workspace,
    product: `CRM`,
    feature: `action-center`,
    active: !!location.pathname.match(/^\/action-center\/?/),
  }
}

export function relatedContactsPrototypeItem(input: NavItemsInput): NavItemType {
  const { workspace, location } = input
  return {
    name: `Related Contacts`,
    icon: () => createElement(UsersThreeIcon, { size: 20, weight: `bold` }),
    href: `/related-contacts`,
    if: !!workspace,
    product: `CRM`,
    feature: `related-contacts-prototype`,
    active: !!location.pathname.match(/^\/related-contacts\/?/),
  }
}

export function companiesItem(input: NavItemsInput): NavItemType {
  const { user, counts, location } = input
  return {
    name: `Companies`,
    icon: () => createElement(BuildingOfficeIcon, { size: 20, weight: `bold` }),
    role: `orgUser`,
    permission: [`org:pages:companies`],
    if: !user.workspaceId,
    active: !!location.pathname.match(/^\/companies\/?/),
    children: [
      {
        name: `Workspaces`,
        href: `/companies/workspaces`,
        if: !user.workspaceId,
        active: !!location.pathname.match(/^\/companies\/workspaces\/?/),
        count: counts.workspaces?.[`all`],
      },
      {
        name: `Locations`,
        href: `/companies/locations`,
        if: !user.workspaceId,
        active: !!location.pathname.match(/^\/companies\/locations\/?/),
        count: counts.locations?.[`all`],
      },
    ],
  }
}

export function pipelinesItem(input: NavItemsInput): NavItemType {
  const { user, pipelines, counts, location } = input
  return {
    name: `Pipelines`,
    icon: () => createElement(CardsThreeIcon, { size: 20, weight: `bold` }),
    if: !!user.workspaceId,
    product: `CRM`,
    permission: [`org:pages:pipelines`, `pages:pipelines`],
    active: !!location.pathname.match(/^\/pipelines\/?/),
    children:
      pipelines.length > 0
        ? [
            ...pipelines.map((pipeline) => ({
              name: pipeline.name,
              href: `/pipelines?tab=${pipeline.id}`,
              active: !!location.pathname.match(/^\/pipelines\/?/) && location.search.includes(`tab=${pipeline.id}`),
              count: counts.pipelines?.[pipeline.id!],
              linkedToActionCenter: isSystemPipelineId(pipeline.id),
            })),
          ]
        : [],
    createPipeline: true,
  }
}

export function inboxItem(input: NavItemsInput): NavItemType {
  const { user, products, counts, location } = input
  return {
    href: `/inbox`,
    name: `Inbox`,
    icon: () => createElement(ChatsIcon, { size: 20, weight: `bold` }),
    if: !!products?.includes(`Inbox`) && !!user.workspaceId,
    product: `CRM`,
    permission: [`org:pages:inbox`, `pages:inbox`],
    active: !!location.pathname.match(/^\/inbox\/?/),
    count: counts.inbox,
    countColor: counts.inbox && counts.inbox > 0 ? `bg-primary-600 px-1.5 text-white` : `hidden`,
  }
}

export function analyticsChildren(input: NavItemsInput): NavItemType[] {
  const { user, location, isIntegrated } = input
  return [
    {
      name: `Marketing`,
      href: `/analytics/marketing${isSMCOrg(user.orgId) ? `?channel=Digital Marketing` : ``}`,
      active: !!location.pathname.match(/^\/analytics\/marketing\/?/),
      permission: [`org:pages:analytics:marketing`, `pages:analytics`],
    },
    {
      name: `Attribution`,
      href: `/analytics/attribution`,
      active: !!location.pathname.match(/^\/analytics\/attribution\/?/),
      permission: [`org:pages:analytics:attribution`, `pages:analytics`],
      if: !isSMCOrg(user.orgId),
    },
    {
      name: `New Patients`,
      href: `/analytics/new-patients`,
      active: !!location.pathname.match(/^\/analytics\/new-patients\/?/),
      permission: [`org:pages:analytics:new-patients`, `pages:analytics`],
      isLocked: !isIntegrated,
    },
    {
      name: `Financial`,
      href: `/analytics/financial`,
      active: !!location.pathname.match(/^\/analytics\/financial\/?/),
      permission: [`org:pages:analytics:revenue`, `pages:analytics`],
      feature: `revenue-dashboard`,
      isLocked: !isIntegrated,
    },
    {
      name: `Conversions`,
      href: `/analytics/conversions`,
      active: !!location.pathname.match(/^\/analytics\/conversions\/?/),
      if: user.type === `super`,
      superUserOnly: true,
    },
  ]
}

/** The Agents tabs, in order. A product hides the tab where no location has it; super-only tabs crown. */
export const AGENTS_HUB_PAGES: {
  name: string
  href: string
  match: RegExp
  product?: LocationProductType | LocationProductType[]
  superOnly?: boolean
}[] = [
  { name: `Voice`, href: `/agents/voice`, match: /^\/agents\/voice(\/|$)/, product: `AI Agent` },
  { name: `SMS`, href: `/agents/sms`, match: /^\/agents\/sms(\/|$)/, product: `SMS Agent` },
  {
    name: `Availability`,
    href: `/agents/availability`,
    match: /^\/agents\/availability\/?$/,
    product: `AI Agent`,
  },
  {
    name: `Settings`,
    href: `/agents/settings`,
    match: /^\/agents\/settings\/?$/,
    product: [`AI Agent`, `SMS Agent`],
  },
]

export function agentsHubItem(input: NavItemsInput): NavItemType {
  const { location } = input
  const { pathname } = location
  return {
    name: `Agents`,
    icon: () => createElement(AgentsIcon, { className: `h-5 w-5` }),
    active: pathname.startsWith(`/agents/`) && !pathname.startsWith(`/agents/fake-contacts`),
    children: AGENTS_HUB_PAGES.map(({ name, href, match, superOnly, product }) => ({
      name,
      href,
      active: match.test(pathname),
      superUserOnly: superOnly,
      product,
    })),
  }
}

export function workflowsItem(input: NavItemsInput): NavItemType {
  const { workflowsEnabled, location } = input
  return {
    name: `Workflows`,
    icon: () => createElement(GitForkIcon, { size: 20, weight: `bold` }),
    href: `/workflows`,
    if: !!workflowsEnabled,
    active: !!location.pathname.match(/^\/workflows\/?/),
  }
}

export function settingsItem(input: NavItemsInput): NavItemType {
  const { user, workspace, location, isOrgUser } = input
  const isUserOrgLevel = isOrgUser !== undefined ? isOrgUser : hasRole(user, `orgUser`)
  return {
    name: `Settings`,
    if: true,
    icon: () => createElement(GearIcon, { size: 20, weight: `bold` }),
    permission: [`org:pages:settings`, `pages:settings`],
    active: !!location.pathname.match(/^\/(settings|imports)(\/.*)?/),
    children: [
      {
        name: `Profile`,
        href: `/settings/users/${user.id}`,
        active: !!location.pathname.match(
          new RegExp(`^/settings/users/${user.id}(/(details|activity|profile|workspace))?$`),
        ),
        if: isUserOrgLevel ? user.workspaceId == null : true,
        product: `CRM`,
      },
      {
        name: `Notifications`,
        href: `/settings/users/${user.id}/notifications`,
        if: !!user.workspaceId && !isUserOrgLevel,
        product: `Call Processing`,
        active: !!location.pathname.match(new RegExp(`^/settings/users/${user.id}/notifications$`)),
        role: `workspaceUser`,
      },
      {
        // Super admins get their own notification menu, but only in org view
        // (no workspace selected): their Agent Diagnostics alerts cover every org,
        // so a workspace view has nothing for them. Mutually exclusive with the
        // workspace-user item above, since supers are always org-level.
        name: `Notifications`,
        href: `/settings/users/${user.id}/notifications`,
        if: user.type === `super` && user.workspaceId == null,
        superUserOnly: true,
        active: !!location.pathname.match(new RegExp(`^/settings/users/${user.id}/notifications$`)),
      },
      {
        name: `Workspace`,
        href: `/settings/workspace`,
        if: user.workspaceId != null,
        permission: [`settings:workspace`],
        role: isOrgUser ? undefined : `workspaceAdmin`,
        active: !!location.pathname.match(/^\/settings\/workspace\/?/),
      },
      {
        name: `Locations`,
        href: `/settings/location-settings`,
        if: user.workspaceId != null,
        role: isOrgUser ? undefined : `workspaceAdmin`,
        permission: [`settings:location`],
        active: !!location.pathname.match(/^\/settings\/location-settings\/?/),
      },
      {
        name: `Users`,
        href: `/settings/users`,
        role: `workspaceAdmin`,
        permission: [`users:manage`, `org:settings:manage-users`],
        active:
          !!location.pathname.match(/^\/settings\/users\/?$/) ||
          (!!location.pathname.match(/^\/settings\/users\//) &&
            !location.pathname.match(new RegExp(`^/settings/users/${user.id}(/.*)?$`))),
      },
      {
        name: `Staff`,
        href: `/settings/staff`,
        // if: user.workspaceId != null && user.role! >= legacyRoleRank.workspaceAdmin,
        if: user.workspaceId != null,
        permission: [`staff:manage`],
        product: `CRM`,
        active: !!location.pathname.match(/^\/settings\/staff\/?/),
      },
      {
        name: `Roles`,
        href: `/settings/roles`,
        permission: [`org:settings:manage-roles`, `roles:manage`],
        product: `CRM`,
        active: !!location.pathname.match(/^\/settings\/roles\/?/),
      },
      {
        name: `Mapping`,
        href: `/settings/mapping`,
        permission: `settings:mapping:view`,
        role: `workspaceUser`,
        if: user.workspaceId != null && !isSMCOrg(user.orgId),
        product: `CRM`,
        active: !!location.pathname.match(/^\/settings\/mapping\/?/),
      },
      {
        name: `Referral Type Mapping`,
        href: `/settings/referral-types`,
        role: `workspaceUser`,
        if: (user.workspaceId != null || workspace != null) && isSMCOrg(user.orgId),
        product: `CRM`,
        active: !!location.pathname.match(/^\/settings\/referral-types\/?/),
      },
      {
        name: `Tags`,
        href: `/settings/tags`,
        permission: `settings:tags:view` as const,
        role: `workspaceUser`,
        product: `CRM`,
        active: !!location.pathname.match(/^\/settings\/tags\/?/),
      },
      {
        // Deliberately generic: this page will grow beyond SMS templates
        // (task templates, etc.). SMS is its first tenant.
        name: `Templates`,
        href: `/settings/templates`,
        // No permission key yet: the backend SmsTemplate entity (and its
        // permission) is pending; workspaceUser mirrors the Tags audience.
        role: `workspaceUser`,
        product: `CRM`,
        active: !!location.pathname.match(/^\/settings\/templates\/?/),
      },
      {
        name: `Forms`,
        href: `/settings/forms`,
        role: `workspaceUser`,
        product: `CRM`,
        active: !!location.pathname.match(/^\/settings\/forms\/?/),
        feature: `paperwork`,
      },
      {
        name: `Integrations`,
        href: `/settings/integrations`,
        permission: [`org:settings:see-integrations`, `settings:integrations`],
        if:
          user!.role! >= legacyRoleRank.orgUser ||
          (user!.role! >= legacyRoleRank.workspaceOwner && !isSMCOrg(user.orgId)),
        product: [`CRM`, `AI Agent`],
        active: !!location.pathname.match(/^\/settings\/integrations\/?/),
      },
      {
        name: `Sending Domains`,
        href: `/settings/sending-domains`,
        permission: [`settings:workspace`],
        if: user.workspaceId != null,
        active: !!location.pathname.match(/^\/settings\/sending-domains\/?/),
      },
      {
        name: `Online Scheduler`,
        href: `/settings/online-scheduler`,
        permission: `settings:scheduling` as const,
        if: user.workspaceId != null,
        product: `Scheduler`,
        role: `workspaceAdmin`,
        active: !!location.pathname.match(/^\/settings\/online-scheduler\/?/),
      },
      {
        name: `Availability`,
        href: `/settings/availability`,
        if: user.workspaceId != null,
        product: [`Scheduler`, `AI Agent`, `SMS Agent`],
        active: !!location.pathname.match(/^\/settings\/availability\/?/),
        superUserOnly: true,
      },
      {
        name: `Import`,
        href: `/settings/imports`,
        role: `orgUser`,
        if: user.workspaceId == null,
        active: !!location.pathname.match(/^\/settings\/imports\/?/),
      },
    ],
  }
}

export function opsItem(input: NavItemsInput): NavItemType {
  const { user, location } = input
  return {
    name: `Ops`,
    icon: () => createElement(WrenchIcon, { size: 20, weight: `bold` }),
    if: user.type === `super`,
    superUserOnly: true,
    active: !!location.pathname.match(/^\/(ops|agents|settings\/feature-toggles)\/?/),
    children: [
      {
        name: `Search Tool`,
        href: `/ops/searchtool`,
        active: !!location.pathname.match(/^\/ops\/searchtool\/?/),
        superUserOnly: true,
      },
      {
        name: `Prompts`,
        href: `/ops/prompts`,
        active: !!location.pathname.match(/^\/ops\/prompts\/?/),
        superUserOnly: true,
      },
      {
        name: `Datasets`,
        href: `/ops/datasets`,
        active: !!location.pathname.match(/^\/ops\/datasets\/?/),
        superUserOnly: true,
      },
      {
        name: `Evals`,
        href: `/ops/evals`,
        active: !!location.pathname.match(/^\/ops\/evals\/?/),
        superUserOnly: true,
      },
      { divider: true, superUserOnly: true },
      {
        name: `Fake Contacts`,
        href: `/agents/fake-contacts`,
        active: !!location.pathname.match(/^\/agents\/fake-contacts\/?/),
        superUserOnly: true,
      },
      { divider: true, superUserOnly: true },
      {
        name: `Extraction`,
        href: `/ops/extraction`,
        active: !!location.pathname.match(/^\/ops\/extraction\/?$/),
        superUserOnly: true,
      },
      { divider: true, superUserOnly: true },
      {
        name: `Redis Cache`,
        href: `/ops/cache`,
        active: !!location.pathname.match(/^\/ops\/cache\/?/),
        superUserOnly: true,
      },
      {
        name: `Pipeline Reconcile`,
        href: `/ops/pipeline-reconcile`,
        active: !!location.pathname.match(/^\/ops\/pipeline-reconcile\/?/),
        superUserOnly: true,
      },
      { divider: true, superUserOnly: true },
      {
        name: `Feature Toggles`,
        href: `/settings/feature-toggles`,
        active: !!location.pathname.match(/^\/settings\/feature-toggles\/?/),
        superUserOnly: true,
      },
      {
        name: `Stored Credentials`,
        href: `/settings/stored-credentials`,
        active: !!location.pathname.match(/^\/settings\/stored-credentials\/?/),
        superUserOnly: true,
      },
      { divider: true, superUserOnly: true },
      {
        name: `Usage Tracking`,
        href: `/ops/usage-tracking`,
        active: !!location.pathname.match(/^\/ops\/usage-tracking\/?/),
        superUserOnly: true,
      },
    ],
  }
}

export function getSideBarItems(input: NavItemsInput): NavItemType[] {
  if (!input.user || input.user?.id == null) return []
  const { location } = input
  return [
    actionCenterItem(input),
    relatedContactsPrototypeItem(input),
    companiesItem(input),
    recordTypeGroup(RECORD_TYPES.contacts, input, [
      RECORD_TYPES.contacts.all(input),
      ...RECORD_TYPES.contacts.presets(input),
    ]),
    recordTypeGroup(RECORD_TYPES.activities, input, [
      RECORD_TYPES.activities.all(input),
      ...RECORD_TYPES.activities.presets(input),
    ]),
    pipelinesItem(input),
    recordTypeGroup(RECORD_TYPES.tasks, input, [...RECORD_TYPES.tasks.presets(input), RECORD_TYPES.tasks.all(input)]),
    inboxItem(input),
    {
      name: `Analytics`,
      icon: () => createElement(ChartLineIcon, { size: 20, weight: `bold` }),
      product: `CRM`,
      // No parent permission: children gate access individually and the parent hides when none remain.
      active: !!location.pathname.match(/^\/analytics\/?/),
      children: analyticsChildren(input),
    },
    agentsHubItem(input),
    workflowsItem(input),
    settingsItem(input),
    opsItem(input),
  ]
}
